import React, { useState, useEffect, useCallback, useId } from 'react';
import axios from 'axios';
import ApplicationRequestForm from './ApplicationRequestForm';
import { useIsNarrow, gridCols, LoadError, activateProps } from './ui';
// DATEDAY: date-only values shown as their calendar day in every timezone.
import { toDisplayDate } from './dateOnly';

const API = '/api';

const STATUS_META = {
  submitted:       { label: 'Submitted',       bg: '#f3f4f6', color: '#374151' },
  under_review:    { label: 'Under Review',     bg: '#dbeafe', color: '#1d4ed8' },
  approved:        { label: 'Approved',         bg: '#d1fae5', color: '#065f46' },
  rejected:        { label: 'Rejected',         bg: '#fee2e2', color: '#991b1b' },
  in_development:  { label: 'In Development',   bg: '#ede9fe', color: '#5b21b6' },
  in_testing:      { label: 'In Testing',       bg: '#fef3c7', color: '#92400e' },
  live:            { label: 'Live',             bg: '#bbf7d0', color: '#166534' },
  ko:              { label: 'KO',               bg: '#fecaca', color: '#7f1d1d' },
};

const PRIORITY_META = {
  low:      { label: 'Low',      color: '#6b7280' },
  medium:   { label: 'Medium',   color: '#2563eb' },
  high:     { label: 'High',     color: '#d97706' },
  critical: { label: 'Critical', color: '#dc2626' },
};

const STATUS_TABS = [
  { key: '',              label: 'All' },
  { key: 'submitted',     label: 'Submitted' },
  { key: 'under_review',  label: 'Under Review' },
  { key: 'in_development',label: 'In Development' },
  { key: 'in_testing',    label: 'In Testing' },
  { key: 'live',          label: 'Live' },
  { key: 'rejected',      label: 'Rejected' },
];

function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status, bg: '#f3f4f6', color: '#374151' };
  return (
    <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
      background: m.bg, color: m.color, whiteSpace: 'nowrap' }}>
      {m.label}
    </span>
  );
}

function PriorityBadge({ priority }) {
  const m = PRIORITY_META[priority] || { label: priority, color: '#374151' };
  return <span style={{ fontSize: 13, fontWeight: 600, color: m.color }}>{m.label}</span>;
}

function fmt(d) {
  if (!d) return '-';
  return toDisplayDate(d).toLocaleDateString('en-GB');
}

// ─── Detail panel ──────────────────────────────────────────

// SHOWREFUSEDBTN: `isCoo` is a fixed boolean passed down from each mount site, not derived
// in-file, because each shell that mounts this dashboard already knows unconditionally which
// role it serves - Dashboard.js is always the coo shell, TechnologyView is always the technology
// shell. Approve/Reject/Mark Live/KO are coo-only server-side (_require_coo); a technology
// account could see all four here before this fix and every one of them 403'd. This is DISPLAY
// ONLY: the backend gate is unchanged and remains the real authority, proven by API-level tests
// showing the four still 403 a technology account regardless of what renders.
function DetailPanel({ req, currentUser, isCoo, onClose, onRefresh }) {
  // RESPONSIVE Phase 2: the two-column info grid reflows to one column below ~900px.
  const isNarrow = useIsNarrow();
  const gridStyle = { ...d.grid, gridTemplateColumns: gridCols(isNarrow, '1fr 1fr') };
  const [assignEmail, setAssignEmail] = useState(req.assigned_to || '');
  // A11YLABEL: one id prefix per component INSTANCE, so two copies of this
  // form on one page cannot produce duplicate ids.
  const a11yId = useId();
  const [assignTeam, setAssignTeam] = useState(req.assigned_to_team || false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [koReason, setKoReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [showKO, setShowKO] = useState(false);
  // APPREQASSIGN item 1: the approve form's OWN recipient choice, deliberately separate state from
  // the standalone Assign control's (assignEmail/assignTeam) - the two controls stay independent,
  // Assign keeps existing unchanged for CHANGING the recipient after the fact (item 4).
  const [approveEmail, setApproveEmail] = useState('');
  const [approveTeam, setApproveTeam] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // APPREQROLE item 5: real technology-role accounts, fetched from the backend, instead of a
  // free-text email input where a typo silently created an unresolvable assignee.
  const [technologyAccounts, setTechnologyAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);

  useEffect(() => {
    axios.get(`${API}/application-requests/technology-accounts`, { params: { caller: currentUser } })
      .then(res => setTechnologyAccounts(res.data || []))
      .catch(() => setTechnologyAccounts([]))
      .finally(() => setLoadingAccounts(false));
  }, [currentUser]);

  const call = async (method, path, data) => {
    setSaving(true);
    setError(null);
    try {
      await axios[method](`${API}/application-requests/${req.id}/${path}?caller=${encodeURIComponent(currentUser)}`, data);
      onRefresh();
      onClose();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Error');
    }
    setSaving(false);
  };

  const assign = () => call('patch', 'assign', { assigned_to: assignEmail || null, assigned_to_team: assignTeam });
  // APPREQASSIGN items 1+2: ONE call approves AND assigns. The recipient is picked here, in the
  // approve form, and travels in the approve body - there is no second assign request that could
  // fail on its own and leave the request approved with nobody named. The button below is also
  // disabled until a recipient is chosen, but that is only UX: the server refuses a recipientless
  // approve with a 400 regardless, and is the actual enforcement point.
  const approveHasRecipient = !!approveEmail || approveTeam;
  const approve = () => {
    if (!approveHasRecipient) {
      setError('Choose who will do this work before approving.');
      return;
    }
    call('patch', 'approve', { assigned_to: approveTeam ? null : (approveEmail || null), assigned_to_team: approveTeam });
  };
  const reject = () => {
    if (!rejectionReason) { setError('Please enter a rejection reason.'); return; }
    call('patch', 'reject', { rejection_reason: rejectionReason });
  };
  const toTesting = () => call('patch', 'testing', {});
  const toLive = () => call('patch', 'live', {});
  const ko = () => {
    if (!koReason) { setError('Please enter a KO reason.'); return; }
    call('patch', 'ko', { ko_reason: koReason });
  };

  const { status } = req;

  return (
    <div style={d.overlay}>
      <div style={d.panel}>
        <div style={d.panelHeader}>
          <div>
            <StatusBadge status={status} />
            <h2 style={d.panelTitle}>{req.title}</h2>
          </div>
          <button style={d.closeBtn} onClick={onClose}>✕</button>
        </div>

        {error && <div style={d.error}>{error}</div>}

        <div style={gridStyle}>
          <div style={d.infoBlock}>
            <span style={d.infoLabel}>Submitted by</span>
            <span style={d.infoVal}>{req.submitted_by}</span>
          </div>
          <div style={d.infoBlock}>
            <span style={d.infoLabel}>Department</span>
            <span style={d.infoVal}>{req.department}</span>
          </div>
          <div style={d.infoBlock}>
            <span style={d.infoLabel}>Estimated users</span>
            <span style={d.infoVal}>{req.estimated_users ?? '-'}</span>
          </div>
          <div style={d.infoBlock}>
            <span style={d.infoLabel}>Submitted</span>
            <span style={d.infoVal}>{fmt(req.created_at)}</span>
          </div>
        </div>

        <div style={d.section}>
          <span style={d.sectionLabel}>Description</span>
          <p style={d.text}>{req.description}</p>
        </div>

        <div style={d.section}>
          <span style={d.sectionLabel}>Problem to solve</span>
          <p style={d.text}>{req.problem_to_solve}</p>
        </div>

        {req.expected_benefit && (
          <div style={d.section}>
            <span style={d.sectionLabel}>Expected benefit</span>
            <p style={d.text}>{req.expected_benefit}</p>
          </div>
        )}

        {req.users_concerned && (
          <div style={d.section}>
            <span style={d.sectionLabel}>Users concerned</span>
            <p style={d.text}>{req.users_concerned}</p>
          </div>
        )}

        {req.dependencies && (
          <div style={d.section}>
            <span style={d.sectionLabel}>Dependencies</span>
            <p style={d.text}>{req.dependencies}</p>
          </div>
        )}

        {req.systems && (
          <div style={d.section}>
            <span style={d.sectionLabel}>Systems concerned</span>
            <p style={d.text}>{req.systems}</p>
          </div>
        )}

        {req.attachment_file_name && (
          <div style={d.section}>
            <span style={d.sectionLabel}>Attachment</span>
            <p style={{ ...d.text, color: '#2563eb' }}>{req.attachment_file_name}</p>
          </div>
        )}

        {req.rejection_reason && (
          <div style={{ ...d.section, backgroundColor: '#fff5f5', borderRadius: 8, padding: '12px 16px' }}>
            <span style={{ ...d.sectionLabel, color: '#991b1b' }}>Rejection reason</span>
            <p style={d.text}>{req.rejection_reason}</p>
          </div>
        )}

        {req.ko_reason && (
          <div style={{ ...d.section, backgroundColor: '#fff5f5', borderRadius: 8, padding: '12px 16px' }}>
            <span style={{ ...d.sectionLabel, color: '#7f1d1d' }}>KO reason</span>
            <p style={d.text}>{req.ko_reason}</p>
          </div>
        )}

        {/* ─ Assignment ─ */}
        {(status === 'submitted' || status === 'under_review' || status === 'in_development') && (
          <div style={d.actionBox}>
            <span style={d.actionTitle}>Assignment</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* APPREQROLE item 5: real technology-role accounts only, via a select, so a typo
                  can no longer create an unresolvable assignee. */}
              {loadingAccounts ? (
                <span style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>Loading technology accounts…</span>
              ) : technologyAccounts.length === 0 ? (
                <span style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>
                  No technology accounts exist yet - ask an admin to create one before assigning.
                </span>
              ) : (
                <select style={{ ...d.input, flex: 1, minWidth: 200 }} value={assignEmail}
                  onChange={e => setAssignEmail(e.target.value)} disabled={assignTeam}>
                  <option value="">Unassigned</option>
                  {technologyAccounts.map(a => (
                    <option key={a.email} value={a.email}>{a.email}</option>
                  ))}
                </select>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151', cursor: 'pointer' }}>
                <input type="checkbox" checked={assignTeam}
                  onChange={e => setAssignTeam(e.target.checked)} style={{ accentColor: '#1a1a2e' }} />
                Notify the Technology team
              </label>
              <button style={d.actionBtn} onClick={assign} disabled={saving}>Assign</button>
            </div>
          </div>
        )}

        {/* ─ Approve: the recipient is chosen HERE and travels in the approve call itself ─
            APPREQASSIGN item 1. One click approves and assigns together. Reject sits alongside it
            and needs NO recipient at all (item 3) - it is unchanged. */}
        {isCoo && (status === 'submitted' || status === 'under_review') && (
          <div style={{ ...d.actionBox, backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
            <span style={d.actionTitle}>Approve and assign</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
              {loadingAccounts ? (
                <span style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>Loading technology accounts…</span>
              ) : technologyAccounts.length === 0 ? (
                <span style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>
                  No technology accounts exist yet - ask an admin to create one, or approve to the whole team.
                </span>
              ) : (
                <select style={{ ...d.input, flex: 1, minWidth: 200 }} value={approveEmail}
                  onChange={e => setApproveEmail(e.target.value)} disabled={approveTeam}>
                  <option value="">Choose who will do this work…</option>
                  {technologyAccounts.map(a => (
                    <option key={a.email} value={a.email}>{a.email}</option>
                  ))}
                </select>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151', cursor: 'pointer' }}>
                <input type="checkbox" checked={approveTeam}
                  onChange={e => setApproveTeam(e.target.checked)} style={{ accentColor: '#065f46' }} />
                Whole Technology team
              </label>
            </div>
            {!approveHasRecipient && (
              <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 10px 0' }}>
                Pick a technology account or the whole team - an approved request must have an owner.
              </p>
            )}
            <button style={{ ...d.approveBtn, opacity: approveHasRecipient ? 1 : 0.5 }}
              onClick={approve} disabled={saving || !approveHasRecipient}>
              Approve and assign
            </button>
          </div>
        )}

        {/* ─ P actions ─ */}
        <div style={d.actionsRow}>
          {/* SHOWREFUSEDBTN: Reject, Mark Live and KO are coo-only server-side (_require_coo) -
              hidden here for anyone else. Move to Testing is UNCHANGED: technology genuinely
              holds it (_require_technology_or_coo), so it carries no isCoo gate at all. */}
          {isCoo && (status === 'submitted' || status === 'under_review') && (
            <button style={d.dangerBtn} onClick={() => setShowReject(r => !r)}>Reject</button>
          )}
          {status === 'in_development' && (
            <button style={d.actionBtn} onClick={toTesting} disabled={saving}>Move to Testing</button>
          )}
          {isCoo && status === 'in_testing' && (
            <>
              <button style={d.approveBtn} onClick={toLive} disabled={saving}>Mark Live</button>
              <button style={d.dangerBtn} onClick={() => setShowKO(r => !r)}>KO</button>
            </>
          )}
        </div>

        {showReject && (
          <div style={d.reasonBox}>
            <label style={d.sectionLabel} htmlFor={`${a11yId}-rejection-reason`}>Rejection reason *</label>
            
            <textarea id={`${a11yId}-rejection-reason`} style={d.textarea} rows={3} value={rejectionReason}
              onChange={e => setRejectionReason(e.target.value)}
              placeholder="Explain why this request is being rejected…" />
            <button style={d.dangerBtn} onClick={reject} disabled={saving}>Confirm Rejection</button>
          </div>
        )}

        {showKO && (
          <div style={d.reasonBox}>
            <label style={d.sectionLabel} htmlFor={`${a11yId}-ko-reason`}>KO reason *</label>
            
            <textarea id={`${a11yId}-ko-reason`} style={d.textarea} rows={3} value={koReason}
              onChange={e => setKoReason(e.target.value)}
              placeholder="What issue was found in testing?" />
            <button style={d.dangerBtn} onClick={ko} disabled={saving}>Confirm KO</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main dashboard ──────────────────────────────────────────

// APPREQASSIGN item 6: the assigned-requests section a technology account sees above the full
// queue. Two clearly separated lists - what is assigned to them PERSONALLY, and what is assigned
// to the WHOLE TEAM - so a person can tell at a glance which of the two a row is. Anything
// assigned to a DIFFERENT individual technology person never appears: the server's ?scope=mine
// filter returns only assigned_to == me OR assigned_to_team, so this component never has to
// filter out somebody else's work itself.
function AssignedToMeSection({ currentUser, onSelect }) {
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    axios.get(`${API}/application-requests/`, { params: { caller: currentUser, scope: 'mine' } })
      .then(res => { setMine(res.data || []); setFailed(false); })
      .catch(() => { setMine([]); setFailed(true); })
      .finally(() => setLoading(false));
  }, [currentUser]);

  const personal = mine.filter(r => r.assigned_to === currentUser);
  // A row with BOTH a named assignee and the team flag belongs to the named person, matching the
  // server's own precedence (_notify_assignee emails assigned_to when set, the team only when not).
  const team = mine.filter(r => !r.assigned_to && r.assigned_to_team);

  const rows = (list, emptyText) => (
    list.length === 0
      ? <p style={{ ...t.empty, padding: '14px 0', textAlign: 'left' }}>{emptyText}</p>
      : (
        <table style={t.table}>
          <tbody>
            {/* KBDACCESS: row-click was the only way to open a request. The onClick and the
                style are untouched (no visual change); activateProps only adds Tab reachability
                and Enter/Space, opening exactly the record a click opens. */}
            {list.map(req => (
              <tr key={req.id} style={t.tr} onClick={() => onSelect(req)}
                {...activateProps(() => onSelect(req), { label: `Open request: ${req.title}` })}>
                <td style={{ ...t.td, fontWeight: 500 }}>{req.title}</td>
                <td style={t.td}>{req.department}</td>
                <td style={t.td}>{req.submitted_by}</td>
                <td style={t.td}><StatusBadge status={req.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )
  );

  return (
    <div style={{ ...t.tableWrap, padding: '18px 20px', marginBottom: 24 }}>
      <h2 style={{ fontSize: 16, fontWeight: 600, color: '#1a1a2e', margin: '0 0 4px 0' }}>My assigned requests</h2>
      <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 14px 0' }}>
        Requests assigned to you, and requests assigned to the whole Technology team.
      </p>
      {loading ? (
        <p style={{ ...t.empty, padding: '14px 0', textAlign: 'left' }}>Loading…</p>
      ) : failed ? (
        <p style={{ ...t.empty, padding: '14px 0', textAlign: 'left' }}>
          Could not load your assigned requests. The full queue below is unaffected.
        </p>
      ) : (
        <>
          <span style={d.sectionLabel}>Assigned to you</span>
          {rows(personal, 'Nothing is assigned to you personally right now.')}
          <div style={{ height: 16 }} />
          <span style={d.sectionLabel}>Assigned to the Technology team</span>
          {rows(team, 'Nothing is assigned to the whole Technology team right now.')}
        </>
      )}
    </div>
  );
}

function ApplicationRequestDashboard({ currentUser, onBack, showAssignedToMe = false, isCoo = false }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  // MAINLISTFAILSAFE: distinct from `loading`. A failed load left `requests` at its empty-array
  // default, rendering "No requests found." indistinguishable from a genuinely empty queue.
  const [loadFailed, setLoadFailed] = useState(false);
  const [activeTab, setActiveTab] = useState('');
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setLoadFailed(false);
    const params = activeTab ? { caller: currentUser, status: activeTab } : { caller: currentUser };
    axios.get(`${API}/application-requests/`, { params })
      .then(res => setRequests(res.data))
      .catch(() => { setRequests([]); setLoadFailed(true); })
      .finally(() => setLoading(false));
  }, [currentUser, activeTab]);

  useEffect(() => { load(); }, [load]);

  const refreshAndClose = () => { setSelected(null); load(); };

  if (showForm) {
    return <ApplicationRequestForm currentUser={currentUser} onBack={() => { setShowForm(false); load(); }} />;
  }

  return (
    <div style={t.container}>
      {onBack && (
        <button
          style={t.backBtn}
          onClick={onBack}
          onMouseEnter={e => { e.currentTarget.style.color = '#c9a84c'; }}
          onMouseLeave={e => { e.currentTarget.style.color = '#1a1a2e'; }}
        >
          ← Back to dashboard
        </button>
      )}
      {selected && (
        <DetailPanel
          req={selected}
          currentUser={currentUser}
          isCoo={isCoo}
          onClose={() => setSelected(null)}
          onRefresh={refreshAndClose}
        />
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 style={t.title}>Application Requests</h1>
          <p style={t.subtitle}>{requests.length} request{requests.length !== 1 ? 's' : ''}</p>
        </div>
        <button style={t.newBtn} onClick={() => setShowForm(true)}>+ New Request</button>
      </div>

      {/* APPREQASSIGN item 6: a PERSISTENT section, not another button - a technology account's own
          work should be visible the moment they open Application Requests, above the full queue
          they also still see (their list-all rights from APPREQROLE are untouched). */}
      {showAssignedToMe && <AssignedToMeSection currentUser={currentUser} onSelect={setSelected} />}

      <div style={t.tabs}>
        {STATUS_TABS.map(tab => (
          <button key={tab.key} style={{ ...t.tab, ...(activeTab === tab.key ? t.tabActive : {}) }}
            onClick={() => setActiveTab(tab.key)}>
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p style={t.empty}>Loading…</p>
      ) : loadFailed ? (
        // MAINLISTFAILSAFE: checked before the empty-state branch below, so a failed load can
        // never render as "No requests found."
        <LoadError message="Could not load the requests." onRetry={load} />
      ) : requests.length === 0 ? (
        <p style={t.empty}>No requests found.</p>
      ) : (
        <div style={t.tableWrap}>
          <table style={t.table}>
            <thead>
              <tr>
                <th style={t.th}>Title</th>
                <th style={t.th}>Submitted by</th>
                <th style={t.th}>Department</th>
                <th style={t.th}>Status</th>
                <th style={t.th}>Assigned to</th>
              </tr>
            </thead>
            <tbody>
              {/* KBDACCESS: same fix as the section table above, same record opened. */}
              {requests.map(req => (
                <tr key={req.id} style={t.tr} onClick={() => setSelected(req)}
                  {...activateProps(() => setSelected(req), { label: `Open request: ${req.title}` })}>
                  <td style={{ ...t.td, fontWeight: 500 }}>{req.title}</td>
                  <td style={t.td}>{req.submitted_by}</td>
                  <td style={t.td}>{req.department}</td>
                  <td style={t.td}><StatusBadge status={req.status} /></td>
                  <td style={t.td}>
                    {req.assigned_to_team
                      ? <span style={{ fontSize: 12, color: '#5b21b6', fontWeight: 500 }}>Technology Team</span>
                      : req.assigned_to
                        ? <span style={{ fontSize: 12 }}>{req.assigned_to}</span>
                        : <span style={{ fontSize: 12, color: '#9ca3af' }}>Unassigned</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const t = {
  container: { fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', maxWidth: '1200px', margin: '0 auto', padding: '24px', backgroundColor: '#f8f9fa' },
  backBtn: { background: 'none', border: 'none', color: '#1a1a2e', fontSize: '14px', cursor: 'pointer', padding: '0 0 16px 0', display: 'block' },
  header: { marginBottom: '20px' },
  title: { fontSize: '24px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 4px 0' },
  subtitle: { fontSize: '14px', color: '#888', margin: 0 },
  tabs: { display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' },
  tab: { padding: '6px 14px', borderRadius: 20, border: '1px solid #d1d5db', background: '#fff', fontSize: 13, cursor: 'pointer', color: '#374151' },
  tabActive: { background: '#1a1a2e', color: '#fff', borderColor: '#1a1a2e' },
  tableWrap: { backgroundColor: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '12px 14px', borderBottom: '2px solid #e5e7eb', fontSize: 12, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #f3f4f6', cursor: 'pointer', transition: 'background 0.1s' },
  td: { padding: '12px 14px', fontSize: 14, color: '#1a1a2e', verticalAlign: 'middle' },
  newBtn: { backgroundColor: '#1a1a2e', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap' },
  empty: { color: '#9ca3af', fontStyle: 'italic', textAlign: 'center', padding: '40px 0' },
};

const d = {
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', justifyContent: 'flex-end', zIndex: 1000 },
  panel: { width: '520px', maxWidth: '95vw', backgroundColor: '#fff', height: '100%', overflowY: 'auto', padding: '28px', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)' },
  panelHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, gap: 12 },
  panelTitle: { fontSize: 20, fontWeight: 600, color: '#1a1a2e', margin: '8px 0 0 0' },
  closeBtn: { background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#6b7280', flexShrink: 0 },
  error: { backgroundColor: '#fde8e8', color: '#c0392b', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px', marginBottom: 20, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: 8 },
  infoBlock: { display: 'flex', flexDirection: 'column', gap: 3 },
  infoLabel: { fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' },
  infoVal: { fontSize: 14, color: '#1a1a2e', fontWeight: 500 },
  section: { marginBottom: 16 },
  sectionLabel: { display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 },
  text: { fontSize: 14, color: '#374151', margin: 0, lineHeight: 1.6, whiteSpace: 'pre-wrap' },
  actionBox: { backgroundColor: '#f8f9fa', borderRadius: 8, padding: '14px 16px', marginBottom: 16 },
  actionTitle: { display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px' },
  input: { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, outline: 'none' },
  actionsRow: { display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 },
  actionBtn: { backgroundColor: '#1a1a2e', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', fontSize: 13, cursor: 'pointer' },
  approveBtn: { backgroundColor: '#065f46', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', fontSize: 13, cursor: 'pointer' },
  dangerBtn: { backgroundColor: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', fontSize: 13, cursor: 'pointer' },
  reasonBox: { backgroundColor: '#fff5f5', border: '1px solid #fecaca', borderRadius: 8, padding: '14px 16px', marginTop: 12 },
  textarea: { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 10 },
};

export default ApplicationRequestDashboard;
